import { useCallback, useEffect, useState } from 'react'
import { Col, Form, Modal, NavDropdown, Row, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { changePasswordPost, resetPasswordChange } from '../api'
import { authSelector } from '../reducers'
import { Button } from './Button'
import { IconEye, IconEyeOff } from './icons'

export function ChangePassword({ mode = 'change', token, show: externalShow, onClose }) {
	const { t } = useTranslation()
	const isChangeMode = mode === 'change'

	const auth = useSelector(authSelector)

	const [internalShow, setInternalShow] = useState(false)
	const show = externalShow !== undefined ? externalShow : internalShow

	const [currentPassword, setCurrentPassword] = useState('')
	const [newPassword, setNewPassword] = useState('')
	const [confirmPassword, setConfirmPassword] = useState('')
	const [error, setError] = useState('')
	const [success, setSuccess] = useState(null)

	const [showCurrentPassword, setShowCurrentPassword] = useState(false)
	const [showNewPassword, setShowNewPassword] = useState(false)
	const [showConfirmPassword, setShowConfirmPassword] = useState(false)

	useEffect(() => {
		if (show) {
			setCurrentPassword('')
			setNewPassword('')
			setConfirmPassword('')
			setError('')
			setSuccess(null)
		}
	}, [show])

	const handleClose = useCallback(() => {
		if (!isChangeMode) {
			const cleanUrl = `${window.location.origin}/`
			window.history.replaceState({}, document.title, cleanUrl)
		}

		if (onClose) onClose()
		else setInternalShow(false)
	}, [isChangeMode, onClose])

	const handleShow = () => {
		if (!externalShow) setInternalShow(true)
	}

	const handleSave = async () => {
		if ((isChangeMode && currentPassword === '') || newPassword === '' || confirmPassword === '') {
			setError(t('FillAllFields', 'Täytä kaikki kentät'))
			return
		}

		if (newPassword !== confirmPassword) {
			setError(t('PasswordsDontMatch', 'Salasanat eivät täsmää'))
			return
		}

		setError('')

		try {
			if (isChangeMode) {
				const data = {
					username: auth.user_name,
					passwordCurrent: currentPassword,
					passwordNew: newPassword,
				}
				const result = await changePasswordPost(data)
				setSuccess(result.data)
			} else {
				const data = {
					newPassword,
					token,
				}

				const result = await resetPasswordChange(data)

				const successOk = result.data?.status === 'Password reset successful'
				setSuccess(successOk)

				if (successOk) {
					setTimeout(() => {
						handleClose()
					}, 3000)
				}
			}
		} catch {
			setError(t('PasswordChangeFailed', 'Salasanan vaihto epäonnistui.'))
		}
	}

	const isSaveDisabled = (isChangeMode && currentPassword === '') || newPassword === '' || confirmPassword === ''

	useEffect(() => {
		if (!isChangeMode && success === true) {
			const timer = setTimeout(() => {
				handleClose()
			}, 3000)

			return () => clearTimeout(timer)
		}
	}, [success, isChangeMode, handleClose])

	return (
		<>
			{isChangeMode && (
				<NavDropdown.Item onClick={handleShow}>{t('ChangePassword', 'Vaihda salasana')}</NavDropdown.Item>
			)}

			{show && (
				<Modal show onHide={handleClose} backdrop="static" size="xl" centered>
					<Modal.Header closeButton>
						<Modal.Title>
							{isChangeMode ? t('ChangePassword', 'Vaihda salasana') : t('ResetPassword', 'Aseta uusi salasana')}
						</Modal.Title>
					</Modal.Header>

					<Modal.Body className="h-100" style={{ minHeight: '70vh' }}>
						<Row className="h-100">
							<Col md={6} className="d-flex flex-column justify-content-center px-5">
								<div
									className="w-100"
									style={{ maxWidth: '400px', margin: '0 auto', marginTop: isChangeMode ? '10vh' : '0' }}
								>
									<h4 className="mb-4">
										{isChangeMode ? t('ChangePassword', 'Vaihda salasana') : t('ResetPassword', 'Aseta uusi salasana')}
									</h4>

									{isChangeMode && (
										<Form.Group className="mb-3">
											<Form.Label>{t('CurrentPassword', 'Nykyinen salasana')}</Form.Label>
											<div className="input-group">
												<Form.Control
													type={showCurrentPassword ? 'text' : 'password'}
													value={currentPassword}
													onChange={(e) => setCurrentPassword(e.target.value)}
												/>
												<button
													className="btn"
													type="button"
													onClick={() => setShowCurrentPassword(!showCurrentPassword)}
												>
													{showCurrentPassword ? <IconEyeOff /> : <IconEye />}
												</button>
											</div>
										</Form.Group>
									)}

									<Form.Group className="mb-3">
										<Form.Label>{t('NewPassword', 'Uusi salasana')}</Form.Label>
										<div className="input-group">
											<Form.Control
												type={showNewPassword ? 'text' : 'password'}
												value={newPassword}
												onChange={(e) => setNewPassword(e.target.value)}
											/>
											<button className="btn" type="button" onClick={() => setShowNewPassword(!showNewPassword)}>
												{showNewPassword ? <IconEyeOff /> : <IconEye />}
											</button>
										</div>
									</Form.Group>

									<Form.Group className="mb-4">
										<Form.Label>{t('ConfirmPassword', 'Vahvista salasana')}</Form.Label>
										<div className="input-group">
											<Form.Control
												type={showConfirmPassword ? 'text' : 'password'}
												value={confirmPassword}
												onChange={(e) => setConfirmPassword(e.target.value)}
											/>
											<button
												className="btn"
												type="button"
												onClick={() => setShowConfirmPassword(!showConfirmPassword)}
											>
												{showConfirmPassword ? <IconEyeOff /> : <IconEye />}
											</button>
										</div>
									</Form.Group>

									{error && <div className="text-danger mb-3">{error}</div>}
									{success === true && (
										<div className="text-success mb-3">
											{t('PasswordChangeSuccess', 'Salasanan vaihto onnistui')}{' '}
											<Spinner animation="border" role="status" variant="primary" size="sm" />
										</div>
									)}

									<div className="d-flex gap-2 mt-2">
										<Button variant="primary" onClick={handleSave} disabled={isSaveDisabled}>
											{isChangeMode
												? t('ChangePassword', 'Vaihda salasana')
												: t('SetPasswordAndLogin', 'Aseta ja siirry kirjautumaan')}
										</Button>
										<Button variant="secondary" onClick={handleClose}>
											{t('Cancel', 'Keskeytä')}
										</Button>
									</div>
								</div>
							</Col>

							<Col
								md={6}
								className="d-none d-md-flex align-items-center justify-content-center"
								style={{ backgroundColor: '#f5f6fa' }}
							>
								<div className="w-100 h-100 d-flex align-items-center justify-content-center p-4">
									<img
										src={isChangeMode ? '/passwordchange.png' : '/passwordreset.png'}
										alt="visual"
										className="img-fluid"
										style={{ maxHeight: '80%' }}
									/>
								</div>
							</Col>
						</Row>
					</Modal.Body>
				</Modal>
			)}
		</>
	)
}
